import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PostCard } from "@/components/PostCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Award, Trophy, UserPlus, UserCheck, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, glossary } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("vitrin");
  const [lib, setLib] = useState([]);
  const [libStatus, setLibStatus] = useState("Okudum");
  const [posts, setPosts] = useState([]);

  const load = useCallback(() => { api.get(`/users/${id}/profile`).then((r) => setData(r.data)); }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.is_self) { navigate("/profil", { replace: true }); }
  }, [data, navigate]);

  useEffect(() => {
    if (tab === "kutuphane" && !data?.kutuphane_gizli) api.get(`/users/${id}/books`, { params: { status: libStatus } }).then((r) => setLib(r.data));
    if (tab === "paylasim") api.get(`/users/${id}/posts`).then((r) => setPosts(r.data));
  }, [tab, libStatus, id, data]);

  const toggleFollow = async () => {
    try {
      if (data.is_following) await api.delete(`/users/${id}/follow`);
      else await api.post(`/users/${id}/follow`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="max-w-4xl mx-auto px-6 py-12 text-muted-foreground">Yükleniyor...</div>;
  const { user: u, ap, sayilar, badges, ortak_kitaplar } = data;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-terra mb-6"><ArrowLeft size={16} /> Geri</button>

      <div className="border border-line rounded-2xl overflow-hidden bg-surface">
        <div className="h-24 relative grain bg-ink" style={u.profil_kapak ? { backgroundImage: `url(${u.profil_kapak})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} data-testid="public-cover" />
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10">
            <div className="w-24 h-24 rounded-2xl bg-terra/10 border-4 border-surface flex items-center justify-center overflow-hidden">
              {u.profil_resmi ? <img src={u.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-5xl text-terra">{u.ad_soyad?.[0]}</span>}
            </div>
            <div className="flex-1">
              <h1 className="font-serif text-3xl text-ink">{u.ad_soyad}</h1>
              <p className="text-sm text-ink-2">{u.biyografi || "—"}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={toggleFollow} data-testid="follow-btn"
                className={data.is_following ? "bg-surface-alt text-ink border border-line hover:bg-surface-alt gap-1" : "bg-terra hover:bg-terra-hover text-white gap-1"}>
                {data.is_following ? <><UserCheck size={16} /> Takip Ediliyor</> : <><UserPlus size={16} /> Takip Et</>}
              </Button>
              <Button variant="outline" className="border-line" onClick={() => navigate(`/mesajlar?u=${id}`)} data-testid="msg-btn">Mesaj</Button>
            </div>
          </div>

          <div className="mt-4 p-4 rounded-xl bg-ink text-page flex items-center gap-3">
            <Trophy className="text-gold shrink-0" size={20} />
            <div className="flex-1">
              <div className="flex justify-between text-sm"><span>Seviye {ap.level}: <span className="text-gold">{ap.level_name}</span></span><span className="text-page/70">{ap.total_ap} AP{ap.streak ? ` · 🔥 ${ap.streak}` : ""}</span></div>
              <Progress value={ap.progress} className="h-1.5 mt-2 bg-page/20 [&>div]:bg-gold" />
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4 text-center">
            {[["Okunmuş", sayilar.okunan], ["Okunuyor", sayilar.okunuyor], ["Okunacak", sayilar.okunacak], ["Paylaşım", sayilar.paylasim], ["Takipçi", sayilar.takipci], ["Takip", sayilar.takip]].map(([l, v]) => (
              <div key={l} className="border border-line rounded-lg p-2"><p className="font-serif text-xl text-ink">{v}</p><p className="text-[11px] text-muted-foreground">{l}</p></div>
            ))}
          </div>
        </div>
      </div>

      {ortak_kitaplar?.length > 0 && (
        <div className="border border-line rounded-xl p-4 bg-surface-alt/50 mt-4" data-testid="ortak-kitaplar">
          <p className="text-sm font-medium text-ink">İkinizin de okuduğu {ortak_kitaplar.length} ortak kitap var:</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {ortak_kitaplar.map((b) => <Link key={b.id} to={`/kitap/${b.id}`} className="text-xs text-terra bg-terra/10 border border-terra/20 rounded-full px-3 py-1 hover:bg-terra/20">{b.baslik}</Link>)}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-surface-alt"><TabsTrigger value="vitrin" data-testid="tab-vitrin">Vitrin</TabsTrigger><TabsTrigger value="kutuphane" data-testid="tab-kutuphane">Kütüphane</TabsTrigger><TabsTrigger value="paylasim" data-testid="tab-paylasim">Paylaşımlar</TabsTrigger></TabsList>

        <TabsContent value="vitrin">
          {u.favori_alinti && (
            <div className="border-l-4 border-gold rounded-xl p-6 bg-surface mt-4" data-testid="public-fav-quote">
              <p className="text-xs uppercase tracking-wider text-gold font-bold">Favori Alıntısı</p>
              <p className="font-serif text-xl italic text-ink mt-2 leading-snug">"{u.favori_alinti}"</p>
              {u.favori_alinti_kaynak && <p className="text-sm text-muted-foreground mt-2">— {u.favori_alinti_kaynak}</p>}
            </div>
          )}
          <h3 className="font-serif text-xl text-ink mt-4 mb-3 flex items-center gap-2"><Award className="text-gold" size={18} /> Kazanılan Rozetler</h3>
          {badges.length === 0 ? <p className="text-sm text-muted-foreground">Henüz rozet kazanılmamış.</p> : (
            <div className="flex flex-wrap gap-4">
              {badges.map((b) => (
                <div key={b.kod} className="flex flex-col items-center gap-1 w-24 text-center">
                  <div className="w-16 h-16 rounded-full bg-gold/15 border-2 border-gold/30 flex items-center justify-center text-2xl">🏅</div>
                  <span className="text-xs text-ink-2">{b.ad}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kutuphane">
          {data.kutuphane_gizli ? (
            <p className="text-sm text-muted-foreground mt-6 flex items-center gap-2"><Lock size={16} /> Bu kullanıcı kütüphanesini gizli tutmayı tercih ediyor.</p>
          ) : (
            <>
              <div className="flex gap-2 mt-4 mb-3">
                {["Okudum", "Okuyorum", "Okuyacağım"].map((s) => (
                  <button key={s} onClick={() => setLibStatus(s)} className={`text-sm px-3 py-1.5 rounded-full border ${libStatus === s ? "bg-terra text-white border-terra" : "border-line text-ink-2"}`}>{s}</button>
                ))}
              </div>
              <div className="space-y-2">
                {lib.length === 0 && <p className="text-sm text-muted-foreground">Kitap yok.</p>}
                {lib.map((b) => (
                  <Link key={b.id} to={`/kitap/${b.id}`} className="flex items-center justify-between border border-line rounded-md p-2.5 bg-surface hover:bg-surface-alt">
                    <div><p className="text-sm font-medium text-ink">{b.baslik}</p><p className="text-xs text-muted-foreground">{b.yazar}</p></div>
                    {b.benim_puan ? <span className="text-xs text-gold">★ {b.benim_puan}</span> : null}
                  </Link>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="paylasim">
          <div className="space-y-4 mt-4">
            {posts.length === 0 && <p className="text-sm text-muted-foreground">Henüz paylaşım yok.</p>}
            {posts.map((p) => <PostCard key={p.id} post={p} glossary={glossary} onDelete={() => {}} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
